
import React, { useState, useEffect } from 'react'
import { withStyles, createStyles } from '@mui/styles'
import { muiOptions, MuiProps } from '../../../infrastructure/materialUiThemeProvider'
import { t } from '../../../infrastructure/i18nextHelper'
import { api } from '../../../infrastructure/api'
import guid, { Guid } from '../../../infrastructure/guid'
import { ExcelGeneratorContainer } from '../../../infrastructure/excelExport'
import { ColumnDescriptor, TableItem, TextField, NumberField, Select } from '../../common/customComponents'
import { MasterDataItem, MasterDataShell, createExcelLines } from './masterDataShell'
import { SnackbarContainer } from '../../../infrastructure/snackbars'
import { applyFilters } from '../../common/filtersHelper'
import { hasClaim } from '../../../infrastructure/signIn/userContext'
import { Claims } from '../../../infrastructure/signIn/models'
import { Company, DutyStatus } from './models'

type ChannelCustomerSegment = {
    id: Guid
    company: string
    channel: string
    customerSegment: string
    dutyStatus: string
    order: number
}

type ChannelCustomerSegmentFilters = {
    company: string | null
    channel: string | null
    customerSegment: string | null
    dutyStatus: string | null
}

let noFilters: ChannelCustomerSegmentFilters = {
    company: null,
    channel: null,
    customerSegment: null,
    dutyStatus: null
}

function CustomerSegmentMasterData({ classes }: MuiProps) {
    let excelGenerator = ExcelGeneratorContainer.useContainer()
    let [segments, setSegments] = useState<TableItem<MasterDataItem<ChannelCustomerSegment>>[]>([])
    let [selectedSegments, setSelectedSegments] = useState<TableItem<MasterDataItem<ChannelCustomerSegment>>[]>([])
    let [companys, setCompanys] = useState<Company[]>([])
    let [dutyStatus, setDutyStatus] = useState<DutyStatus[]>([])
    let [filters, setFilters] = useState<ChannelCustomerSegmentFilters>(noFilters)
    let snackbar = SnackbarContainer.useContainer()

    const tBase = 'admin.masterdata.channelCustomerSegment.'

    let init = async () => {
        let companies = api.get<Company[]>('masterdata/productCompany/company')
        let companiesPromise = await companies
        setCompanys(companiesPromise)
        setDutyStatus(companiesPromise.flatMap(x => x.dutyStatuses).distinct())
        await load()
    }

    let load = async () => {
        let data = await api.get<ChannelCustomerSegment[]>('masterdata/customerSegment/channelCustomerSegment')
        setSegments(data.map(toTableItem))
    }

    useEffect(() => {
        init()
    }, [])

    let getItems = () => applyFilters(segments, filters)

    let onSave = async (item: ChannelCustomerSegment & IsNew) => {
        await api.post('masterdata/customerSegment/channelCustomerSegment', item)
        await load()
        return true
    }

    let onDelete = async (ids: string[] | null) => {
        if (ids == null) return false
        await api.del('masterdata/customerSegment/channelCustomerSegment', ids)
        await load()
        return true
    }

    let exportExcel = () => {
        excelGenerator.generate({
            filename: 'CustomerSegments.xlsx',
            sheets: [{
                name: 'CustomerSegments',
                lines: createExcelLines(selectedSegments, columns)
            }]
        })
    }

    let importExcel = async (file: Blob) => {
        try {
            await api.upload('masterdata/customerSegment/import/channelCustomerSegments', file, 'file')
            await load()
            snackbar.success(t('httpSuccess.importSuccessful'))
        } catch (error) {
            console.error('Import failed:', error)
        }
    }

    let columns: ColumnDescriptor<TableItem<MasterDataItem<ChannelCustomerSegment>>>[] = [
        {
            name: t(tBase + 'company'),
            value: x => x.company,
            columnFilter: { value: filters.company ?? '', onChange: (company: string | null) => setFilters({ ...filters, company }) }
        },
        {
            name: t(tBase + 'channel'),
            value: x => x.channel,
            columnFilter: { value: filters.channel ?? '', onChange: (channel: string | null) => setFilters({ ...filters, channel }) }
        },
        {
            name: t(tBase + 'customerSegment'),
            value: x => x.customerSegment,
            columnFilter: { value: filters.customerSegment ?? '', onChange: (customerSegment: string | null) => setFilters({ ...filters, customerSegment }) }
        },
        {
            name: t(tBase + 'dutyStatus'),
            value: x => x.dutyStatus,
            columnFilter: { value: filters.dutyStatus ?? '', onChange: (dutyStatus: string | null) => setFilters({ ...filters, dutyStatus }) }
        },
        {
            name: t(tBase + 'order'),
            value: x => x.order.toString()
        }
    ]

     let isManager = hasClaim(Claims.MasterdataCustomerSegmentManager)

    return (
        <div className={classes.container}>
            <MasterDataShell
                tableId={'customerSegment-table'}
                headerLabel={t(tBase + 'customerSegments')}
                itemLabel={t(tBase + 'customerSegment')}
                isManager={isManager}
                onExportExcel={exportExcel}
                onImportExcel={importExcel}
                onNew={() => emptySegment()}
                onSave={onSave}
                onDelete={onDelete}
                items={getItems()}
                onSelectionChange={setSelectedSegments}
                columns={columns}>{
                    (selectedItem, setSelectedItem) => (
                        <>
                             <Select label={t('admin.masterdata.productCompany.company')}
                                disabled={!isManager}
                                value={selectedItem.company}
                                choices={companys.map(x => ({ value: x.code, text: x.name }))}
                                onChange={val => { if (val) setSelectedItem({ ...selectedItem, company: val }) }} />
                            <TextField label={t(tBase + 'channel')}
                                text={selectedItem.channel}
                                onChange={event => setSelectedItem({ ...selectedItem, channel: event.target.value })} />
                            <TextField label={t(tBase + 'customerSegment')}
                                text={selectedItem.customerSegment}
                                onChange={event => setSelectedItem({ ...selectedItem, customerSegment: event.target.value })} />
                            <Select label={t(tBase + 'dutyStatus')}
                                disabled={!isManager}
                                value={selectedItem.dutyStatus}
                                choices={dutyStatus.map(x => ({ value: x, text: x }))}
                                onChange={val => { if (val) setSelectedItem({ ...selectedItem, dutyStatus: val }) }} />
                            <NumberField label={t(tBase + 'order')}
                                text={selectedItem.order}
                                precision={2}
                                onChange={value => setSelectedItem({ ...selectedItem, order: value })} />
                        </>
                    )}
            </MasterDataShell>
        </div>
    )
}

type IsNew = { isNew: boolean }

let emptySegment = (): ChannelCustomerSegment & IsNew => ({
    id: guid.empty,
    company: '',
    channel: '',
    customerSegment: '',
    dutyStatus: '',
    order: 1,
    isNew: true
})

let toTableItem = (segment: ChannelCustomerSegment): TableItem<MasterDataItem<ChannelCustomerSegment>> => ({
    ...segment,
    id: segment.id,
    isModified: false
})

let styles = () =>
    createStyles({
        container: { height: '100%' }
    })

export default withStyles(styles, muiOptions)(CustomerSegmentMasterData)
